import React, { useState, useEffect } from 'react';
import { Button, Modal, Select, AutoComplete } from 'antd';
import { ClockCircleOutlined, CloseOutlined } from '@ant-design/icons';
import * as API from '../../utils/api';
import { notifyApiError, notifySuccess } from '../../utils/notification';

const { Option } = Select;

const TimeClockButton = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isClocked, setIsClocked] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [currentTimerId, setCurrentTimerId] = useState(null);
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    const savedTimer = localStorage.getItem('activeTimer');
    if (savedTimer) {
      const { timerId, isActive } = JSON.parse(savedTimer);
      if (isActive) {
        setIsClocked(true);
        setCurrentTimerId(timerId);
      }
    }
  }, []);

  useEffect(() => {
    if (isModalVisible && !isClocked) {
      fetchReports();
    }
  }, [isModalVisible, isClocked]);

  const fetchReports = async () => {
    try {
      const response = await API.getReportsData('', 0, 100);
      if (response.success) {
        setReports(response.reports || []);
      }
    } catch (error) {
      notifyApiError('Failed to load reports');
    }
  };

  const showModal = () => setIsModalVisible(true);
  const handleCancel = () => setIsModalVisible(false);

  const toggleClock = async () => {
    try {
      if (isClocked) {
        const response = await API.stopTimeTracking(currentTimerId);
        if (response.success) {
          setIsClocked(false);
          setCurrentTimerId(null);
          setSelectedReport(null);
          localStorage.removeItem('activeTimer');
          notifySuccess('Timer stopped');
        } else {
          notifyApiError(response.error || 'Failed to stop timer');
        }
      } else {
        const body = selectedReport ? { report_id: selectedReport } : {};
        const response = await API.startTimeTracking(body);
        if (response.success) {
          setIsClocked(true);
          setCurrentTimerId(response.data.id);
          localStorage.setItem(
            'activeTimer',
            JSON.stringify({
              timerId: response.data.id,
              isActive: true,
              startTime: new Date().toISOString(),
            })
          );
          notifySuccess('Timer started');
        } else {
          notifyApiError(response.error || 'Failed to start timer');
        }
      }
    } catch (error) {
      notifyApiError('Timer operation failed');
    }
  };

  if (isMinimized) {
    return (
      <Button
        type="primary"
        shape="circle"
        size="small"
        onClick={() => setIsMinimized(false)}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 1000,
        }}
      ><ClockCircleOutlined /></Button>
    );
  }

  return (
    <>
      <div style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 1000 }}>
        <Button
          type="danger"
          shape="circle"
          size="small"
          onClick={() => setIsMinimized(true)}
          style={{
            position: 'absolute',
            top: '-8px',
            right: '-8px',
            zIndex: 1001,
          }}
        ><CloseOutlined /></Button>
        <Button
          type={isClocked ? 'danger' : 'primary'}
          size="large"
          onClick={showModal}
          style={{
            width: '260px',
            height: '60px',
            borderRadius: '15px',
          }}>
          {isClocked ? 'Stop Tracking Time' : 'Start Tracking Time'}
        </Button>
      </div>
      <Modal title="Time Clock" visible={isModalVisible} onCancel={handleCancel} footer={null}>
        {!isClocked && (
          <>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Select a Report:</label>
            <AutoComplete
              style={{ width: '100%', marginBottom: '8px' }}
              value={searchValue}
              onChange={setSearchValue}
              onSelect={value => {
                const report = reports.find(report => `Name: ${report.report_name} - ID: ${report.report_uuid}` === value);
                setSelectedReport(report?.id);
                setSearchValue(value);
              }}
              allowClear
              onClear={() => {
                setSelectedReport(null);
                setSearchValue('');
              }}
              dataSource={reports
                .map(report => `Name: ${report.report_name} - ID: ${report.report_uuid}`)
                .filter(item => item.toLowerCase().includes(searchValue.toLowerCase()))}
            />
          </>
        )}
        <Button type={isClocked ? 'danger' : 'primary'} size="large" block onClick={toggleClock}>
          {isClocked ? 'Clock Out' : 'Clock In'}
        </Button>
      </Modal>
    </>
  );
};

export default TimeClockButton;
